'use client';
import {Variant} from "@/types";
import {useEffect, useRef, useState, useCallback, useMemo} from "react";
import {useWindowVirtualizer} from "@tanstack/react-virtual";
import {useWindowSize} from "usehooks-ts";
import {useMediaQuery} from "@/hooks/use-media-query";
import {useSearchParams} from "next/navigation";
import ResponsiveVariantCard from "@/components/variants/responsive-variant-card";
import {VariantSkeletonGrid} from "./variant-skeleton-grid";
import InitialStaticGrid from "./initial-static-grid";

interface VariantGridProps {
    variants: Variant[];
    fetchMoreData?: () => void;
    hasMore?: boolean;
    isLoading?: boolean;
    estimatedTotalHits?: number;
    setRenderFinished?: (arg0: boolean) => void;
    end: boolean;
    stop: boolean;
}

// CSS styles for performance optimization (CSS containment).
// Note: contentVisibility:auto was previously set on cards. Removed because
// it duplicated the virtualizer's own off-screen culling and lost the race
// during fast Mobile scroll — empty white gaps appeared while the virtualizer
// rendered the row but the browser content-visibility heuristic decided to
// skip it. The virtualizer alone is sufficient to keep off-screen work down.
const rowStyles = {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    width: '100%',
    willChange: 'transform', // GPU acceleration hint
    contain: 'layout style paint', // CSS containment for isolation
};

const cardContainerStyles = {
    contain: 'layout style paint', // Isolate each card's rendering
};

const VariantGrid = ({variants, fetchMoreData, isLoading, estimatedTotalHits, setRenderFinished, end, stop}: VariantGridProps) => {
    // Track hydration - start false for SSR, becomes true after mount
    const [isHydrated, setIsHydrated] = useState(false);
    // Track if virtualized grid is ready to render (has container width)
    const [isGridReady, setIsGridReady] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const searchParams = useSearchParams();

    // Debounce ref for resize handling
    const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const {width: windowWidth, height: windowHeight} = useWindowSize();
    const minWidths = {
        xs: 160,
        sm: 200,
        md: 200,
        lg: 200,
        xl: 220,
        xxl: 220
    }

    // Memoize matches object — without this, identity changes on every render
    // (literal {}). useCallback(calculateColumnCount, [matches]) below would
    // never preserve identity, defeating the memoization. Booleans from
    // useMediaQuery are primitive, so identity only changes on real breakpoint
    // crossings.
    const xs = useMediaQuery('(max-width: 640px)');
    const sm = useMediaQuery('(min-width: 640px) and (max-width: 768px)');
    const md = useMediaQuery('(min-width: 768px) and (max-width: 1024px)');
    const lg = useMediaQuery('(min-width: 1024px) and (max-width: 1280px)');
    const xl = useMediaQuery('(min-width: 1280px) and (max-width: 1536px)');
    const xxl = useMediaQuery('(min-width: 1536px)');

    const matches = useMemo(
        () => ({xs, sm, md, lg, xl, xxl}),
        [xs, sm, md, lg, xl, xxl]
    );

    // Detect if mobile for adaptive overscan
    const isMobile = matches.xs || matches.sm;

    const containerWidth = containerRef.current?.clientWidth || windowWidth || 0;

    // Set hydrated after mount
    useEffect(() => {
        setIsHydrated(true);
    }, []);

    // Set grid ready when we have a valid container width
    useEffect(() => {
        if (containerWidth > 0) {
            setIsGridReady(true);
        }
    }, [containerWidth]);

    const calculateColumnCount = useCallback((innerWidth: number) => {
        if (innerWidth <= 0) return 2; // Default fallback for SSR
        const activeBreakpoint = Object.keys(matches).find((key) => matches[key as keyof typeof matches]) ?? 'xs';
        const count = Math.floor(innerWidth / (minWidths[activeBreakpoint as keyof typeof minWidths]));
        return Math.max(1, count); // Ensure at least 1 column
    }, [matches]);

    // Safe calculations - ensure valid values for virtualizer
    const columnCount = calculateColumnCount(containerWidth) || 2;
    const rowCount = variants.length > 0 ? Math.ceil(variants.length / columnCount) : 0;
    const itemWidth = columnCount > 0 ? containerWidth / columnCount : 170;
    const itemHeight = itemWidth > 0 ? itemWidth * 1.4 : 343;

    // Track scrollMargin as state so it can be updated when modal closes
    // This is needed because offsetTop can change when body overflow changes
    const [scrollMargin, setScrollMargin] = useState(0);

    // Update scrollMargin when container is available, AND re-measure when the
    // page layout shifts (curated-intro expandable toggling, sticky filter
    // bar height changes, etc.). ResizeObserver on document.body catches all
    // these — previously the effect only ran once on mount and went stale.
    useEffect(() => {
        if (!containerRef.current) return;

        const updateMargin = () => {
            if (containerRef.current) {
                setScrollMargin(containerRef.current.offsetTop);
            }
        };

        updateMargin();

        const ro = new ResizeObserver(updateMargin);
        ro.observe(document.body);
        return () => ro.disconnect();
    }, [isHydrated, isGridReady]);

    // Window virtualizer for row-based virtualization
    // Adaptive overscan: 3 for mobile (less memory), 5 for desktop (smoother scroll)
    const rowVirtualizer = useWindowVirtualizer({
        count: rowCount,
        estimateSize: () => itemHeight || 343,
        overscan: isMobile ? 3 : 5,
        scrollMargin: scrollMargin,
    });

    // virtualItems / lastVisibleIndex computed once per render. lastVisibleIndex
    // is a primitive, so effects depending on it only re-run when the visible
    // window's last row actually changes (not on every scroll-Y delta).
    const virtualItems = rowVirtualizer.getVirtualItems();
    const lastVisibleIndex = virtualItems.length > 0
        ? virtualItems[virtualItems.length - 1].index
        : -1;
    const hasVirtualItems = virtualItems.length > 0;

    // Signal render finished when virtualizer is ready
    useEffect(() => {
        if (hasVirtualItems) {
            setRenderFinished?.(true);
        }
    }, [hasVirtualItems, setRenderFinished]);

    // Infinite scroll - check if near bottom.
    // Dependency uses the primitive lastVisibleIndex instead of the array
    // returned by getVirtualItems() (whose identity changes on every scroll
    // frame ~60Hz). Drops the effect from per-frame to once-per-row-boundary.
    useEffect(() => {
        if (!fetchMoreData || isLoading || stop || end) return;
        if (lastVisibleIndex < 0) return;

        // Calculate if we're near the bottom (within ~1000px equivalent in rows)
        const rowsFromBottom = rowCount - lastVisibleIndex - 1;
        const pixelsFromBottom = rowsFromBottom * itemHeight;

        if (pixelsFromBottom < 1000) {
            fetchMoreData();
        }
    }, [lastVisibleIndex, rowCount, itemHeight, fetchMoreData, isLoading, stop, end]);

    // Debounced re-measure on window resize (150ms delay to avoid excessive recalculations)
    useEffect(() => {
        if (resizeTimeoutRef.current) {
            clearTimeout(resizeTimeoutRef.current);
        }

        resizeTimeoutRef.current = setTimeout(() => {
            rowVirtualizer.measure();
        }, 150);

        return () => {
            if (resizeTimeoutRef.current) {
                clearTimeout(resizeTimeoutRef.current);
            }
        };
    }, [windowWidth, windowHeight, rowVirtualizer]);

    // Immediate re-measure on search params change (no debounce needed)
    useEffect(() => {
        rowVirtualizer.measure();
    }, [searchParams?.get('variantId'), rowVirtualizer]);

    // Before hydration OR before grid is ready: render static CSS grid
    // This prevents layout shift by keeping products visible while measuring container width
    if (!isHydrated || !isGridReady) {
        return (
            <div className={'grow w-full'} ref={containerRef}>
                <InitialStaticGrid
                    variants={variants}
                />
            </div>
        );
    }

    const virtualRows = virtualItems;

    // No variants found
    if (variants.length === 0) {
        return (
            <div className={'grow w-full'} ref={containerRef}>
                <NoVariantsFound />
            </div>
        );
    }

    return (
        <div className={'grow w-full'} ref={containerRef}>
            <div
                style={{
                    height: `${rowVirtualizer.getTotalSize()}px`,
                    width: '100%',
                    position: 'relative',
                }}
            >
                {virtualRows.map((virtualRow) => {
                    const rowStartIndex = virtualRow.index * columnCount;

                    return (
                        <div
                            key={virtualRow.key}
                            style={{
                                ...rowStyles,
                                height: `${virtualRow.size}px`,
                                transform: `translateY(${virtualRow.start - rowVirtualizer.options.scrollMargin}px)`,
                            }}
                        >
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: `repeat(${columnCount}, 1fr)`,
                                    gap: '4px',
                                    width: '100%',
                                    height: '100%',
                                }}
                            >
                                {Array.from({ length: columnCount }).map((_, columnIndex) => {
                                    const itemIndex = rowStartIndex + columnIndex;
                                    if (itemIndex >= variants.length) return <div key={columnIndex} />;

                                    const variant = variants[itemIndex];
                                    if (!variant) return <div key={columnIndex} />;

                                    const isFirstRow = virtualRow.index === 0;

                                    return (
                                        <div
                                            key={`${virtualRow.index}-${columnIndex}-${variant.variant_id}`}
                                            className="transition-opacity duration-400 ease-in-out starting:opacity-0 opacity-100"
                                            style={{
                                                padding: 2,
                                                ...cardContainerStyles,
                                                containIntrinsicSize: `${itemWidth}px ${itemHeight}px`,
                                            }}
                                        >
                                            <ResponsiveVariantCard
                                                variant={variant}
                                                priority={isFirstRow}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default VariantGrid;


export const NoVariantsFound = () => (
    <div className="grow flex-col items-center justify-start max-h-36 text-center py-4 w-full relative transition-opacity duration-400 ease-in-out starting:opacity-0 opacity-100 overflow-hidden">
        <VariantSkeletonGrid count={24} disableAnimation={true}/>
        <div className="absolute top-0 w-full h-36 bg-linear-to-b from-transparent to-gray-100 pt-16">
            <div className="sticky top-12">
                <h4 className="text-lg sm:text-xl md:text-2xl font-extrabold mt-6 text-slate-900">Keine Produkte gefunden</h4>
            </div>
        </div>
    </div>
);
